import api from './api';
import type {
  SubscriptionPlan,
  UserSubscription,
  CreditBalance,
  CreditTransaction,
  CreditPack,
  CreditCosts,
  Invoice,
  SubscribeDto,
  PurchaseCreditsDto,
  SubscriptionResult,
  CreditPurchaseResult,
  PagedResult,
} from '../types/billing';

// ============================================================================
// Subscription API
// ============================================================================

/**
 * Get all available subscription plans
 */
export async function getPlans(): Promise<SubscriptionPlan[]> {
  const response = await api.get<SubscriptionPlan[]>('/subscription/plans');
  return response.data;
}

/**
 * Get a specific plan by code
 */
export async function getPlanByCode(code: string): Promise<SubscriptionPlan> {
  const response = await api.get<SubscriptionPlan>(`/subscription/plans/${code}`);
  return response.data;
}

/**
 * Get current user's subscription
 */
export async function getCurrentSubscription(): Promise<UserSubscription | null> {
  try {
    const response = await api.get<UserSubscription>('/subscription/current');
    return response.data;
  } catch (error: unknown) {
    // 404 means no subscription
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
    }
    throw error;
  }
}

/**
 * Subscribe to a plan
 */
export async function subscribe(dto: SubscribeDto): Promise<SubscriptionResult> {
  const response = await api.post<SubscriptionResult>('/subscription/subscribe', dto);
  return response.data;
}

/**
 * Change subscription plan (upgrade/downgrade)
 */
export async function changePlan(dto: SubscribeDto): Promise<SubscriptionResult> {
  const response = await api.post<SubscriptionResult>('/subscription/change-plan', dto);
  return response.data;
}

/**
 * Cancel current subscription
 */
export async function cancelSubscription(): Promise<SubscriptionResult> {
  const response = await api.post<SubscriptionResult>('/subscription/cancel');
  return response.data;
}

/**
 * Reactivate a cancelled subscription
 */
export async function reactivateSubscription(): Promise<SubscriptionResult> {
  const response = await api.post<SubscriptionResult>('/subscription/reactivate');
  return response.data;
}

/**
 * Get user's plan limits
 */
export async function getPlanLimits(): Promise<SubscriptionPlan | null> {
  try {
    const response = await api.get<SubscriptionPlan>('/subscription/limits');
    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
    }
    throw error;
  }
}

// ============================================================================
// Credits API
// ============================================================================

/**
 * Get current user's credit balance
 * @param bustCache - Add timestamp to prevent caching (default: false)
 */
export async function getCreditBalance(bustCache = false): Promise<CreditBalance> {
  const url = bustCache
    ? `/credits/balance?_t=${Date.now()}`
    : '/credits/balance';
  const response = await api.get<CreditBalance>(url);
  return response.data;
}

/**
 * Get credit transaction history
 */
export async function getCreditHistory(
  pageNumber = 1,
  pageSize = 20
): Promise<PagedResult<CreditTransaction>> {
  const response = await api.get<PagedResult<CreditTransaction>>('/credits/history', {
    params: { pageNumber, pageSize },
  });
  return response.data;
}

/**
 * Get available credit packs
 */
export async function getCreditPacks(): Promise<CreditPack[]> {
  const response = await api.get<CreditPack[]>('/credits/packs');
  return response.data;
}

/**
 * Purchase a credit pack
 */
export async function purchaseCredits(dto: PurchaseCreditsDto): Promise<CreditPurchaseResult> {
  const response = await api.post<CreditPurchaseResult>('/credits/purchase', dto);
  return response.data;
}

/**
 * Get credit costs for all actions
 */
export async function getCreditCosts(): Promise<CreditCosts> {
  const response = await api.get<CreditCosts>('/credits/costs');
  return response.data;
}

/**
 * Get billing configuration (currency)
 */
export async function getBillingConfig(): Promise<{ currency: string }> {
  const response = await api.get<{ currency: string }>('/credits/config');
  return response.data;
}

// ============================================================================
// Billing/Invoices API
// ============================================================================

/**
 * Get user's invoice history
 */
export async function getInvoices(
  pageNumber = 1,
  pageSize = 20
): Promise<PagedResult<Invoice>> {
  const response = await api.get<PagedResult<Invoice>>('/billing/invoices', {
    params: { pageNumber, pageSize },
  });
  return response.data;
}

/**
 * Get a specific invoice
 */
export async function getInvoice(id: number): Promise<Invoice | null> {
  try {
    const response = await api.get<Invoice>(`/billing/invoices/${id}`);
    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
    }
    throw error;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if user has sufficient credits for an action
 */
export async function hasSufficientCredits(requiredCredits: number): Promise<boolean> {
  const balance = await getCreditBalance();
  return balance.totalCredits >= requiredCredits;
}

/**
 * Format subscription status for display
 */
export function getSubscriptionStatusColor(
  status: string
): 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Trialing':
      return 'info';
    case 'PastDue':
      return 'warning';
    case 'Cancelled':
    case 'Expired':
      return 'error';
    default:
      return 'info';
  }
}

/**
 * Get credit balance status (for color coding)
 */
export function getCreditBalanceStatus(
  current: number,
  monthlyAllocation: number
): 'healthy' | 'warning' | 'critical' {
  if (monthlyAllocation === 0) {
    // Free tier or no allocation
    return current > 10 ? 'healthy' : current > 0 ? 'warning' : 'critical';
  }
  const percentage = (current / monthlyAllocation) * 100;
  if (percentage > 50) return 'healthy';
  if (percentage > 20) return 'warning';
  return 'critical';
}

// Export all functions as default object for convenience
const billingService = {
  // Subscription
  getPlans,
  getPlanByCode,
  getCurrentSubscription,
  subscribe,
  changePlan,
  cancelSubscription,
  reactivateSubscription,
  getPlanLimits,
  // Credits
  getCreditBalance,
  getCreditHistory,
  getCreditPacks,
  purchaseCredits,
  getCreditCosts,
  hasSufficientCredits,
  // Billing
  getInvoices,
  getInvoice,
  // Utilities
  getSubscriptionStatusColor,
  getCreditBalanceStatus,
};

export default billingService;
